import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';

import { useBreakpoint } from '@ast/magma/breakpoints';

import { Alert } from '@ast/magma/components/alert';
import { Button } from '@ast/magma/components/button';

import { findFocusableElement, moveNextChildGenerator } from '@ast/magma/utils/focus';

import { FlexContainer } from '@app/common/components/FlexContainer/FlexContainer';
import { FormProviderWrapper } from '@app/common/components/FormProviderWrapper/FormProviderWrapper';
import { OverlaySpinner } from '@app/common/components/overlayspinner';
import { FieldInfoGroupField, isPasswordField } from '@app/common/configurable-wizards';
import { NewPasswordFieldComponent } from '@app/common/configurable-wizards/fields/NewPassword';
import {
  Field, FieldComponent, FieldLocator, WizardStepAlertMessage,
} from '@app/common/configurable-wizards/types';
import { isFieldInfoGroupField } from '@app/common/configurable-wizards/utils';
import { errorsToAlert } from '@app/common/configurable-wizards/utils/wizardAlertError';
import { isEmptyArray, isNotEmptyArray } from '@app/common/utils';
import { useServerFieldsErrors } from '@app/common/utils/form/useServerFieldsErrors';
import { useServerErrors } from '@app/common/utils/serverValidation/useServerErrors';

import { logoutException } from '@app/core/authentication/logout/forceLogout/useForceLogoutSubscription';

import { useUserSettingsLocator } from '@app/widgets/contacts-and-settings/hooks/useUserSettingsLocator';
import { useUserSettingsTranslation } from '@app/widgets/contacts-and-settings/hooks/useUserSettingsTranslation';
import { useSetUserPasswordMutation } from '@app/widgets/contacts-and-settings/queries/queryTyping/setUserPassword';
import { PasswordFields, PasswordResetFields } from '@app/widgets/contacts-and-settings/types';

import styles from './EditPasswordField.pcss';

interface SubmitData {
  readonly [PasswordResetFields.NewUserPassword]: string,
  readonly [PasswordResetFields.OldUserPassword]: string,
}

export const NewPasswordLocator: FieldLocator = ({ field }) => (
  isPasswordField(field) && field.id === PasswordResetFields.NewUserPassword
    ? NewPasswordFieldComponent
    : undefined
);

export const EditPasswordFieldComponent: FieldComponent<FieldInfoGroupField> = ({ field }) => {
  const history = useHistory();
  const isMobile = useBreakpoint().device === 'mobile';
  const { t } = useUserSettingsTranslation();
  const locator = useUserSettingsLocator([
    NewPasswordLocator,
  ]);
  const [alert, setAlert] = useState<WizardStepAlertMessage | null>(null);
  const { formState, ...form } = useForm({
    criteriaMode: 'all',
  });
  const [setUserPasswordMutation, { data, loading, error }] = useSetUserPasswordMutation();

  const submitErrors = data?.setUserPassword?.errors;
  const submitSucceed = data?.setUserPassword?.successful;
  const alertRef = useRef<HTMLDivElement>(null);

  const { fieldErrors, genericErrors } = useServerErrors(submitErrors);
  useServerFieldsErrors(form.setError, fieldErrors);

  // hide alert on submitting
  useEffect(() => {
    if (formState.isSubmitting) {
      setAlert(null);
    }
  }, [formState]);

  // FormProviderWrapper component moves focus to the first element after submit.
  // If the alert is rendered the first element is its close button.
  // So the screenreaders voice this button text instead of alert message.
  // The solution is to remove focus from the button.
  useEffect(() => {
    if (alertRef?.current) {
      const nextChild = moveNextChildGenerator(alertRef?.current, false);
      const element = findFocusableElement(nextChild, true);
      element?.blur();
    }
  }, [alert]);

  // show success password change alert
  useEffect(() => {
    if (submitSucceed) {
      setAlert({
        type: 'success',
        content: t(
          'profile-and-security.edit-password.changed-successfully-message|Message for the success password change alert',
          'Password successfully changed. All active sessions will be terminated',
        ),
      });
    }
  }, [submitSucceed]);

  // clear form state/values after success password updating
  useEffect(() => {
    if (formState.isSubmitSuccessful && isEmptyArray(submitErrors)) {
      form.reset();
    }
  }, [formState, form.reset, submitErrors]);

  // show generic errors in the alert
  useEffect(() => {
    if (isNotEmptyArray(genericErrors)) {
      const errorAlert = errorsToAlert(genericErrors);

      if (errorAlert) {
        setAlert({
          type: errorAlert.type,
          content: errorAlert.content,
          title: errorAlert.title,
        });
      }
    }
  }, [form.setError, genericErrors]);

  if (error) {
    throw error;
  }

  return (
    <>
      <OverlaySpinner visible={loading} />
      <FormProviderWrapper
        formProviderProps={{
          formState,
          ...form,
        }}
        formProps={{
          onSubmit: form.handleSubmit(({ OldUserPassword, NewUserPassword }: SubmitData) => {
            logoutException.setLogoutException(true);
            setUserPasswordMutation({
              variables: {
                data: {
                  oldPassword: OldUserPassword,
                  newPassword: NewUserPassword,
                },
              },
            });
          }),
        }}
      >
        <p className={styles.advice}>
          {t(
            'profile-and-security.edit-password.advice|Represents the password change hint',
            'Make sure to choose a strong password that you\'re not using anywhere else.',
          )}
        </p>

        <FlexContainer direction="column" gap="large">
          {alert && (
            <Alert
              level={alert.type || 'info'}
              title={alert.title || ''}
              ref={alertRef}
              data-stable-name="PasswordChangeAlert"
              showClose={{
                // eslint-disable-next-line i18next/no-literal-string
                'aria-label': t(
                  'profile-and-security.edit-password.close-alert-button.accessibility-text|Accessibility text for the close-alert button',
                  'Close alert',
                ),
                onClose: () => setAlert(null),
              }}
            >
              {alert.content}
            </Alert>
          )}

          <FlexContainer direction="column">
            {field.subFields.map((item) => {
              const subField = item as Field;
              const Component = locator(subField);
              return (
                <Component
                  parent=""
                  field={subField}
                  locator={locator}
                  key={subField.id}
                />
              );
            })}
          </FlexContainer>

          <FlexContainer
            direction={isMobile ? 'column-reverse' : 'row'}
            gap="small"
          >
            <Button
              type="button"
              look="outlined"
              predefinedSize="medium"
              data-stable-name="CancelButton"
              onClick={() => history.goBack()}
            >
              {t(
                'profile-and-security.edit-password.cancel-button.text|Cancel password editing button',
                'Cancel',
              )}
            </Button>
            <Button
              type="submit"
              look="primary"
              predefinedSize="medium"
              data-stable-name="SaveButton"
            >
              {t(
                'profile-and-security.edit-password.save-button.text|Save new password button',
                'Save',
              )}
            </Button>
          </FlexContainer>
        </FlexContainer>
      </FormProviderWrapper>
    </>
  );
};

export const EditPasswordFieldLocator: FieldLocator = ({ field }) => (
  isFieldInfoGroupField(field) && field.id === PasswordFields.PasswordReset
    ? EditPasswordFieldComponent
    : undefined
);
